"use client"
import { useState, useMemo } from "react"
import type { CarbonSinkWithGeo, MapFilters } from "@/types/glassroom"
import { useTranslations } from "next-intl"
import { getCountryCode, getCountryName } from "@forestsync/db"
import {
  Input,
  Button,
  Badge,
  Card,
  Text,
  Select,
  ThemeIcon,
  Group,
  Stack,
  Grid,
  Paper,
  Flex,
  TbMapPin,
  TbFilter,
  TbSearch,
  TbRuler,
  TbChevronDown,
  TbLeaf,
  TbX,
  TbSearchOff,
} from "@forestsync/ui"

interface FilterSearchPanelProps {
  mapData: CarbonSinkWithGeo[]
  filteredData: CarbonSinkWithGeo[]
  filters: MapFilters
  onFiltersChange: (partial: Partial<MapFilters>) => void
  onResetFilters: () => void
  onSelectForest: (id: string) => void
  selectedItem: string | null
  sidebarOpen: boolean
  toggleSidebar: () => void
}

export function FilterSearchPanel({
  mapData,
  filteredData,
  filters,
  onFiltersChange,
  onResetFilters,
  onSelectForest,
  selectedItem,
  sidebarOpen,
  toggleSidebar,
}: FilterSearchPanelProps) {
  const t = useTranslations()
  const [filtersVisible, setFiltersVisible] = useState(false)

  const countries = useMemo(() => {
    const unique = new Set<string>()
    mapData.forEach((f) => unique.add(getCountryCode(f.country)))
    return Array.from(unique).sort()
  }, [mapData])

  const regions = useMemo(() => {
    const unique = new Set<string>()
    mapData.forEach((f) => {
      if (f.region && (!filters.country || getCountryCode(f.country) === filters.country)) {
        unique.add(f.region)
      }
    })
    return Array.from(unique).sort()
  }, [mapData, filters.country])

  const municipalities = useMemo(() => {
    const unique = new Set<string>()
    mapData.forEach((f) => {
      if (
        f.municipality &&
        (!filters.country || getCountryCode(f.country) === filters.country) &&
        (!filters.region || f.region === filters.region)
      ) {
        unique.add(f.municipality)
      }
    })
    return Array.from(unique).sort()
  }, [mapData, filters.country, filters.region])

  const creditTypes = useMemo(() => {
    const unique = new Set<string>()
    mapData.forEach((f) => {
      if (f.creditType) unique.add(f.creditType)
    })
    return Array.from(unique).sort()
  }, [mapData])

  const methodologies = useMemo(() => {
    const unique = new Set<string>()
    mapData.forEach((f) => {
      if (f.methodology && (!filters.creditType || f.creditType === filters.creditType)) {
        unique.add(f.methodology)
      }
    })
    return Array.from(unique).sort()
  }, [mapData, filters.creditType])

  return (
    <>
      {/* Panel container */}
      <div
        className={`
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} 
          transition-transform duration-300 
          absolute md:relative 
          z-20 md:z-auto 
          h-full w-80 lg:w-96 
          border-r bg-background/75 backdrop-blur-sm md:bg-background 
          overflow-auto p-4 flex flex-col
          shadow-xl md:shadow-none
        `}
      >
        <div className="mb-4">
          <Flex justify="space-between" align="center" className="mb-4">
            <Text fw={600} size="lg" className="flex items-center">
              <TbLeaf className="mr-2" size={18} /> {t("map.projectExplorer")}
            </Text>
            <Button
              variant="subtle"
              p="sm"
              onClick={toggleSidebar}
              className="md:hidden"
              aria-label={t("common.close")}
            >
              <TbX size={20} />
            </Button>
          </Flex>

          {/* Search input */}
          <Input
            placeholder={t("map.searchPlaceholder")}
            value={filters.searchTerm}
            onChange={(e) => onFiltersChange({ searchTerm: e.target.value })}
            leftSection={<TbSearch size={16} />}
            radius="md"
            className="mb-4"
          />

          {/* Filters */}
          <Paper
            withBorder
            radius="md"
            className="bg-background mb-4"
            shadow="sm"
          >
            <Button
              variant="subtle"
              fullWidth
              classNames={{
                root: "flex items-center justify-between",
                inner: "w-full flex items-center justify-between",
              }}
              onClick={() => setFiltersVisible(!filtersVisible)}
              radius="md"
              aria-expanded={filtersVisible}
              tabIndex={0}
              rightSection={
                <div
                  className={`transition-transform ${filtersVisible ? "rotate-180" : ""}`}
                >
                  <TbChevronDown size={16} />
                </div>
              }
            >
              <Text fw={500} size="sm" className="flex items-center">
                <TbFilter className="mr-1.5" size={16} /> {t("common.filters")}
              </Text>
            </Button>

            {filtersVisible && (
              <Stack gap="xs" p="md">
                <div>
                  <Select
                    label={t("common.country")}
                    placeholder={t("map.allCountries")}
                    data={[
                      { value: "", label: t("map.allCountries") },
                      ...countries.map((c) => ({ value: c, label: getCountryName(c) })),
                    ]}
                    value={filters.country || ""}
                    onChange={(v) => onFiltersChange({ country: v || null })}
                    searchable
                    clearable
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div>
                  <Select
                    label={t("common.region")}
                    placeholder={t("map.allRegions")}
                    data={[
                      { value: "", label: t("map.allRegions") },
                      ...regions.map((r) => ({ value: r, label: r })),
                    ]}
                    value={filters.region || ""}
                    onChange={(v) => onFiltersChange({ region: v || null })}
                    searchable
                    clearable
                    disabled={!regions.length}
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div>
                  <Select
                    label={t("common.municipality")}
                    placeholder={t("map.allMunicipalities")}
                    data={[
                      { value: "", label: t("map.allMunicipalities") },
                      ...municipalities.map((m) => ({ value: m, label: m })),
                    ]}
                    value={filters.municipality || ""}
                    onChange={(v) => onFiltersChange({ municipality: v || null })}
                    searchable
                    clearable
                    disabled={!municipalities.length}
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div>
                  <Select
                    label={t("map.creditType")}
                    placeholder={t("map.allCreditTypes")}
                    data={[
                      { value: "", label: t("map.allCreditTypes") },
                      ...creditTypes.map((ct) => ({ value: ct, label: ct })),
                    ]}
                    value={filters.creditType || ""}
                    onChange={(v) => onFiltersChange({ creditType: v || null })}
                    searchable
                    clearable
                    disabled={!creditTypes.length}
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div>
                  <Select
                    label={t("map.methodology")}
                    placeholder={t("map.allMethodologies")}
                    data={[
                      { value: "", label: t("map.allMethodologies") },
                      ...methodologies.map((m) => ({ value: m, label: m })),
                    ]}
                    value={filters.methodology || ""}
                    onChange={(v) => onFiltersChange({ methodology: v || null })}
                    searchable
                    clearable
                    disabled={!methodologies.length}
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div>
                  <Select
                    label={t("map.projectType")}
                    placeholder={t("map.allProjectTypes")}
                    data={[
                      { value: "", label: t("map.allProjectTypes") },
                      { value: "1", label: t("map.exAnte") },
                      { value: "2", label: t("map.exPost") },
                    ]}
                    value={filters.projectType || ""}
                    onChange={(v) => onFiltersChange({ projectType: v || null })}
                    className="w-full"
                    size="sm"
                  />
                </div>

                <div className="flex justify-end items-center pt-2">
                  <Button
                    variant="light"
                    size="xs"
                    onClick={onResetFilters}
                    leftSection={<TbFilter size={14} />}
                  >
                    {t("map.resetFilters")}
                  </Button>
                </div>
              </Stack>
            )}
          </Paper>

          {/* Results list heading */}
          <div className="flex justify-between items-center mb-3 mt-4">
            <Text fw={600} size="sm" className="flex items-center">
              <TbLeaf className="mr-1.5 text-forest" size={16} />
              {t("map.results")}
            </Text>
            <Badge size="sm" variant="filled" color="forest" radius="sm">
              {filteredData.length}
            </Badge>
          </div>
        </div>

        {/* Forest List */}
        <div className="flex-1 overflow-auto flex flex-col min-h-0">
          {/* Forest list results */}
          <div className="space-y-2 flex-1 overflow-auto pb-4">
            {filteredData.length === 0 ? (
              <Paper
                withBorder
                p="md"
                className="flex flex-col items-center justify-center h-32 text-center bg-background text-dark-300"
              >
                <TbSearchOff className="h-8 w-8 mb-2" />
                <Text size="sm" fw={500}>
                  {t("map.noMatchingProjects")}
                </Text>
              </Paper>
            ) : (
              <Stack gap={0}>
                {filteredData.map((forest) => (
                  <Card
                    key={forest.refId}
                    className={`cursor-pointer transition-all ${
                      selectedItem === forest.refId
                        ? "border-l-4 border-forest-500 shadow-md bg-forest-50 dark:bg-forest-900/20"
                        : "hover:bg-forest-light-hover border-l-4 border-transparent"
                    }`}
                    onClick={() => onSelectForest(forest.refId)}
                    withBorder
                    padding="sm"
                    radius="sm"
                  >
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                      <ThemeIcon
                        className={
                          selectedItem === forest.refId
                            ? "border-white dark:border-black bg-forest text-background"
                            : "border-forest bg-background text-forest"
                        }
                        size="md"
                        radius="xl"
                      >
                        <TbLeaf size={14} />
                      </ThemeIcon>
                      <div className="flex-1">
                        <Text fw={600} size="sm" lineClamp={1}>
                          {forest.name}
                        </Text>
                        <Group gap="xs" wrap="nowrap">
                          <Text
                            size="xs"
                            className="flex items-center text-gray-700 dark:text-gray-300"
                          >
                            <TbMapPin className="mr-1" size={12} />
                            {`${getCountryName(forest.country)} | ${forest.region}`}
                          </Text>
                        </Group>
                        <Grid gutter="xs" className="mt-1">
                          <Grid.Col span={6}>
                            <Group gap="xs" wrap="nowrap">
                              <TbRuler size={12} />
                              <Text size="xs">
                                {(forest.sizeHa ?? 0).toLocaleString()} ha
                              </Text>
                            </Group>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Group gap="xs" wrap="nowrap">
                              <TbLeaf size={12} />
                              <Text size="xs">
                                {(forest.co2Storage ?? 0).toLocaleString()} t
                              </Text>
                            </Group>
                          </Grid.Col>
                        </Grid>
                      </div>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
